// The live trip: the pack's seed itinerary with the family's edits folded in (shared/trip.ts).
// Pure, so every phone gets the same answer from the same events. Order: synced events by server
// seq, then this phone's unsynced ones by time, so nothing depends on device clocks once synced.
// An edit that would leave something incomplete or broken is skipped, never thrown.
import { z } from 'zod'
import { DayItem, Leg, Place, Stage, type Pack } from './pack/schema'
import { TRIP_EVENTS, type TripEventType } from './trip'

export interface TripEvent {
  id: string
  type: string
  at: number
  seq?: number
  personId: string
  payload: unknown
}

type Entity = Record<string, unknown> & { id: string }

/** Places the family adds may have no write-up yet (a pasted Maps link). */
const FamilyPlace = Place.extend({ summary: z.string() })

/** One kind of thing (stages, legs, items, places): seed values, then per-field writes and tombstones. */
class Table<T extends { id: string }> {
  private rows = new Map<string, { value: Entity; removed: boolean; seeded: boolean }>()
  constructor(seed: readonly T[]) {
    for (const s of seed) this.rows.set(s.id, { value: { ...s }, removed: false, seeded: true })
  }
  set(id: string, fields: Record<string, unknown>) {
    const row = this.rows.get(id) ?? { value: { id }, removed: false, seeded: false }
    const merged: Record<string, unknown> = { ...row.value, ...fields }
    // null clears a field (a surprise switched off, a place unlinked).
    row.value = Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== null)) as Entity
    row.removed = false
    this.rows.set(id, row)
  }
  has(id: string): boolean {
    return this.rows.has(id)
  }
  remove(id: string) {
    const row = this.rows.get(id)
    if (row) row.removed = true
  }
  /** The rows that are still there and complete, as validated by `schema`. */
  list(schema: { safeParse(v: unknown): { success: boolean; data?: unknown } }): T[] {
    const out: T[] = []
    for (const r of this.rows.values()) {
      if (r.removed) continue
      const parsed = schema.safeParse(r.value)
      if (parsed.success) out.push(parsed.data as T)
    }
    return out
  }
}

export function tripEventOrder(a: TripEvent, b: TripEvent): number {
  if (a.seq !== undefined && b.seq !== undefined) return a.seq - b.seq
  if (a.seq !== undefined) return -1
  if (b.seq !== undefined) return 1
  return a.at - b.at || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
}

const isTripEvent = (type: string): type is TripEventType => type in TRIP_EVENTS

export function projectTrip(pack: Pack, events: readonly TripEvent[]): Pack {
  const mine = events.filter((e) => isTripEvent(e.type) && (e.payload as { tripId?: unknown } | null)?.tripId === pack.meta.id)
  if (!mine.length) return pack

  const stages = new Table(pack.itinerary.stages)
  const legs = new Table(pack.itinerary.legs)
  const items = new Table(pack.itinerary.dayItems)
  const places = new Table(pack.places)
  const meta = { ...pack.meta }

  for (const e of [...mine].sort(tripEventOrder)) {
    const parsed = TRIP_EVENTS[e.type as TripEventType].safeParse(e.payload)
    if (!parsed.success) continue
    const p = parsed.data as Record<string, unknown> & { fields?: Record<string, unknown> }
    switch (e.type as TripEventType) {
      case 'trip.meta.set':
        // null clears a field (the reward machine back to the kit's).
        Object.assign(meta, p.fields)
        if (meta.rewardMachine === null) delete meta.rewardMachine
        if (meta.travellers === null) delete meta.travellers
        break
      case 'trip.stage.set':
        stages.set(p.stageId as string, p.fields ?? {})
        break
      case 'trip.stage.removed':
        stages.remove(p.stageId as string)
        break
      case 'trip.leg.set':
        legs.set(p.legId as string, p.fields ?? {})
        break
      case 'trip.leg.removed':
        legs.remove(p.legId as string)
        break
      case 'trip.item.set':
        // Remember who made an item (its first write), for the child edit rules (worker/edits.ts).
        items.set(p.itemId as string, { ...(items.has(p.itemId as string) ? {} : { by: e.personId }), ...p.fields })
        break
      case 'trip.item.removed':
        items.remove(p.itemId as string)
        break
      case 'trip.place.set':
        places.set(p.placeId as string, { summary: '', tips: [], tags: [], ...p.fields })
        break
    }
  }

  const placeList = places.list(FamilyPlace)
  const placeIds = new Set(placeList.map((p) => p.id))
  const stageList = stages.list(Stage).sort((a, b) => a.startDate.localeCompare(b.startDate))
  const dayItems = items
    .list(DayItem)
    .map((i) => (i.placeId && !placeIds.has(i.placeId) ? { ...i, placeId: undefined } : i))
    .sort((a, b) => a.date.localeCompare(b.date) || (a.time ?? '99').localeCompare(b.time ?? '99') || a.id.localeCompare(b.id))
  return {
    ...pack,
    meta,
    // A trip always has at least one stage; an edit that removes them all is ignored.
    itinerary: {
      stages: stageList.length ? stageList : pack.itinerary.stages,
      legs: legs.list(Leg).sort((a, b) => Date.parse(a.departsAt) - Date.parse(b.departsAt)),
      dayItems,
    },
    places: placeList,
  }
}
